import { createSelector } from 'reselect';
import { hasAnyProductsWithScore } from '../product/product.selectors';
import { UserRoles } from '../user/user.constants';
import filter from 'lodash/filter';
import keys from 'lodash/keys';
import groupBy from 'lodash/groupBy';
import without from 'lodash/without';

const authorizedUser = state => state.auth.user;
const allIds = state => state.entities.user.allIds;
const byId = state => state.entities.user.byId;

const channelsById = state => state.entities.channel.byId;
const regionsById = state => state.entities.region.byId;
const teamsById = state => state.entities.team.byId;

export const getCurrentUser = createSelector(
  [authorizedUser, byId, hasAnyProductsWithScore, teamsById, channelsById, regionsById],
  (id, users, hasProductsWithScore, teams, channels, regions) => {
    const user = id ? users[id] : null;
    if (!user) return null;
    const channel = user.channel ? channels[user.channel] : null;
    const region = user.region ? regions[user.region] : null;

    const team = user.team ? teams[user.team] : null;
    const hasAccessToPrizeProgram = hasProductsWithScore || (user && user.role === UserRoles.SUPERUSER);
    const hasAccessToAdmin =
      user &&
      (user.role === UserRoles.SUPERUSER ||
        user.role === UserRoles.SALESMANAGER ||
        user.role === UserRoles.REGIONMANAGER ||
        user.role === UserRoles.TEAMLEADER);

    return {
      ...user,
      team,
      region,
      channel,
      hasAccessToPrizeProgram,
      hasAccessToAdmin,
    };
  }
);

export const getCurrentUserRole = createSelector([getCurrentUser], user => {
  return user ? user.role : null;
});

export const getCurrentUserTeam = createSelector([getCurrentUser, teamsById], (user, teams) => {
  return user && user.team ? teams[user.team] : null;
});

export const getAllUsers = createSelector(
  [byId, allIds, teamsById, channelsById, regionsById],
  (users, ids, teams, channels, regions) => {
    return ids.map(id => {
      const user = users[id];
      return {
        ...user,
        channel: channels[user.channel],
        region: regions[user.region],
        team: teams[user.team] || null,
      };
    });
  }
);

export const getAllUsersByCurrentUserChannel = createSelector([getAllUsers, getCurrentUser], (users, currentUser) => {
  if (!currentUser.channel) {
    return [];
  }
  return filter(users, user => user.channel && user.channel._id === currentUser.channel._id);
});

export const getAllUsersGroupedByChannel = createSelector(
  [byId, channelsById, teamsById, regionsById, getCurrentUser],
  (users, channels, teams, regions, currentUser) => {
    if (keys(channels).length === 0) return [];

    const regionsByChannel = groupBy(regions, region => region.channel);
    const teamsWithRegion = filter(teams, team => !!team.region);
    const teamsWithoutRegion = filter(teams, team => !team.region);
    const teamsWithRegionByChannel = groupBy(teamsWithRegion, team => team.channel);
    const teamsWithoutRegionByChannel = groupBy(teamsWithoutRegion, team => team.channel);

    return keys(channels).map(key => {
      const channel = channels[key];
      const channelRegions = regionsByChannel[key] || [];

      const mappedRegions = channelRegions.map(region => {
        const regionTeams = filter(teamsWithRegionByChannel[key], team => team.region === region._id);

        const regionTeamsMapped = regionTeams.map(team => {
          const members = team.members ? without(team.members.map(id => users[id]), undefined) : [];
          const adminUser = users[team.adminUser];
          const isAdminUserCurrentUser = adminUser && adminUser._id === currentUser._id;

          return {
            ...team,
            adminUser,
            members: without(
              [
                !isAdminUserCurrentUser ? adminUser : undefined,
                ...filter(members, member => member._id !== adminUser._id),
              ],
              undefined
            ),
          };
        });

        return {
          ...region,
          adminUser: users[region.adminUser],
          teams: [...regionTeamsMapped],
        };
      });

      const noRegionTeams = teamsWithoutRegionByChannel[key] || [];
      const noRegionTeamsMapped = noRegionTeams.map(team => {
        const members = team.members ? without(team.members.map(id => users[id]), undefined) : [];
        const adminUser = users[team.adminUser];
        const isAdminUserCurrentUser = adminUser && adminUser._id === currentUser._id;

        return {
          ...team,
          adminUser,
          members: members
            ? without(
                [
                  !isAdminUserCurrentUser ? adminUser : undefined,
                  ...filter(members, member => member._id !== adminUser._id),
                ],
                undefined
              )
            : [],
        };
      });

      const regions = without(
        [
          ...mappedRegions,
          noRegionTeamsMapped.length
            ? {
                _id: 'orphan',
                name: 'Ei aluetta',
                teams: [...noRegionTeamsMapped],
              }
            : undefined,
        ],
        undefined
      );

      return {
        ...channel,
        adminUser: users[channel.adminUser],
        members: filter(users, user => user.channel === key),
        regions,
      };
    });
  }
);

/*

          const noRegionTeams = teamsWithoutRegionByChannel[key] || [];
          const noRegionTeamsMapped = noRegionTeams.map(team => {
            const members = team.members ? without(team.members.map(id => users[id]), undefined) : [];
            const adminUser = users[team.adminUser];
            return {
              ...team,
              adminUser,
              members: without([
                adminUser,
                ...filter(members, member => member._id !== adminUser._id),
              ], undefined),
            };
          });
              ...noRegionTeamsMapped
*/



// WEBPACK FOOTER //
// ./src/modules/user/user.selectors.js